// AudioBus — barramento único de áudio da engine (substitui os stubs paralelos
// useGameAudio/SoundManager). Web Audio API: baixa latência, polifonia nativa,
// ganho por evento e mute global. Eventos sem arquivo mapeado tocam em silêncio
// (no-op), então dá para ligar os sons aos poucos sem quebrar nada.
//
// Contrato e nomes de arquivo seguem docs/MAPA-DE-SOM.md.

export type SoundEvent =
  // UI
  | 'ui.hover' | 'ui.click' | 'ui.back' | 'ui.toggle' | 'ui.blocked'
  // Feedback
  | 'feedback.correct' | 'feedback.wrong' | 'feedback.reveal' | 'feedback.lifeLost' | 'feedback.shield'
  // Reward
  | 'reward.axe' | 'reward.combo' | 'reward.master' | 'reward.purchase' | 'reward.life' | 'reward.daily' | 'reward.checkpoint'
  // Tension
  | 'tension.timerLow' | 'tension.timeout'
  // Stinger
  | 'stinger.phaseStart' | 'stinger.minigame' | 'stinger.dueloProposed' | 'stinger.dueloAccepted'
  | 'stinger.dueloWon' | 'stinger.dueloLost' | 'stinger.minigameWin' | 'stinger.victory'
  | 'stinger.victoryTribunal' | 'stinger.gameOver';

interface Registered {
  file: string; // relativo à base do Vite (import.meta.env.BASE_URL)
  gain: number; // 0..1
  cooldownMs?: number;
}

// Somente os eventos com arquivo presente entram aqui. Os demais permanecem
// silenciosos até receberem áudio (ver docs/MAPA-DE-SOM.md, coluna "Implementado").
const REGISTRY: Partial<Record<SoundEvent, Registered>> = {
  'ui.hover':          { file: 'audio/ui/hover.ogg',           gain: 0.25, cooldownMs: 60 },
  'ui.click':          { file: 'audio/ui/click.ogg',           gain: 0.40 },
  'ui.back':           { file: 'audio/ui/back.ogg',            gain: 0.38 },
  'ui.blocked':        { file: 'audio/ui/blocked.ogg',         gain: 0.50 },
  'feedback.correct':  { file: 'audio/feedback/correct.ogg',   gain: 0.70 },
  'feedback.wrong':    { file: 'audio/feedback/wrong.ogg',     gain: 0.70 },
  'feedback.reveal':   { file: 'audio/feedback/reveal.ogg',    gain: 0.40 },
  'feedback.lifeLost': { file: 'audio/feedback/life-lost.ogg', gain: 0.60 },
};

const BASE = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';

class AudioBusImpl {
  private ctx: AudioContext | null = null;
  private buffers = new Map<SoundEvent, AudioBuffer>();
  private loading = new Map<SoundEvent, Promise<AudioBuffer | null>>();
  private lastPlay = new Map<SoundEvent, number>();
  private muted = false;
  private unlocked = false;

  setMuted(m: boolean) { this.muted = m; }
  isMuted() { return this.muted; }

  /** Cria/retoma o AudioContext. iOS/Android exigem que isto ocorra dentro de
   *  um gesto do usuário — por isso é chamado a partir de hover/click. */
  private ensureCtx(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      try { this.ctx = new AC(); } catch { return null; }
    }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    return this.ctx;
  }

  /** Destrava o áudio no primeiro gesto e pré-carrega os buffers registrados. */
  unlock() {
    const ctx = this.ensureCtx();
    if (!ctx || this.unlocked) return;
    this.unlocked = true;
    (Object.keys(REGISTRY) as SoundEvent[]).forEach((ev) => { void this.load(ev); });
  }

  private load(ev: SoundEvent): Promise<AudioBuffer | null> {
    const entry = REGISTRY[ev];
    if (!entry) return Promise.resolve(null);
    const cached = this.buffers.get(ev);
    if (cached) return Promise.resolve(cached);
    const inflight = this.loading.get(ev);
    if (inflight) return inflight;
    const ctx = this.ensureCtx();
    if (!ctx) return Promise.resolve(null);
    const p = fetch(BASE + entry.file)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error('audio 404: ' + entry.file))))
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => { this.buffers.set(ev, decoded); return decoded; })
      .catch(() => null);
    this.loading.set(ev, p);
    return p;
  }

  play(ev: SoundEvent) {
    if (this.muted) return;
    const entry = REGISTRY[ev];
    if (!entry) return; // evento ainda sem áudio → silêncio

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (entry.cooldownMs && now - (this.lastPlay.get(ev) ?? -Infinity) < entry.cooldownMs) return;
    this.lastPlay.set(ev, now);

    const ctx = this.ensureCtx();
    if (!ctx) return;
    const buf = this.buffers.get(ev);
    if (!buf) { void this.load(ev); return; } // carregando; toca na próxima vez

    try {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const g = ctx.createGain();
      g.gain.value = entry.gain;
      src.connect(g).connect(ctx.destination);
      src.start(0);
    } catch {
      /* nunca deixa o áudio derrubar o jogo */
    }
  }
}

export const AudioBus = new AudioBusImpl();
